import React, { useContext, useState } from 'react';
import { isEmpty } from 'lodash';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import useStyles from './ConfirmationModalStyle';
import Button from '../material-ui-kit/CustomButtons/Button';
import LoadingContext, { LOADING_AREAS } from '../../contexts/LoadingContext';

// Modified by AI on 05/26/2026. Edit #1.
const ConfirmationModal = ({
    className = '',
    title = '',
    message = '',
    confirmButton = true,
    rejectButton = true,
    additionalButton = null,
    rejectButtonLabel = 'CANCEL',
    confirmButtonLabel = 'CONFIRM',
    onRejectButtonClick = () => {},
    onConfirmButtonClick = () => {},
    onConfirmButtonClickAsync,
    onRejectButtonClickAsync,
    rejectButtonLoading = false,
    confirmButtonLoading = false,
    rejectButtonDisabled = false,
    confirmButtonDisabled = false,
    children = null,
    testId,
    footerLeftSideComponent = null,
    preTitle = null,
    titleIcon = null,
    dynamicMessage = {},
    messageClassName = '',
    titleClassName = '',
    footerActions = true,
    smallPadding = false,
    buttonClassName = '',
}) => {
    const shouldWaitUntilActionCompleted = onConfirmButtonClickAsync instanceof Function;
    const shouldWaitUntilRejectActionCompleted = onRejectButtonClickAsync instanceof Function;

    const confirmActionAsync = async () => {
        setIsConfirmInProcess(true);
        if (onConfirmButtonClickAsync instanceof Function) {
            await onConfirmButtonClickAsync();
        }

        setIsConfirmInProcess(false);
    };

    const rejectActionAsync = async () => {
        setIsRejectInProcess(true);
        if (onRejectButtonClickAsync instanceof Function) {
            await onRejectButtonClickAsync();
        }

        setIsRejectInProcess(false);
    };

    const { t } = useTranslation();
    const classes = useStyles({ preTitle, smallPadding });
    const rootClasses = clsx(className, classes.root);
    const messageClasses = clsx(classes.message, messageClassName);
    const titleClasses = clsx(classes.title, titleClassName);

    const { isLoading } = useContext(LoadingContext);
    const [isConfirmInProcess, setIsConfirmInProcess] = useState(false);
    const [isRejectInProcess, setIsRejectInProcess] = useState(false);
    const additionalButtonClassNames = clsx({
        [additionalButton?.classNames]: !!additionalButton?.classNames,
        [classes.actionPanelButton]: !additionalButton?.classNames,
        [buttonClassName]: !buttonClassName,
    });

    return (
        <div data-text-id={testId} data-test-id={testId} className={rootClasses}>
            {preTitle && <p className={classes.preTitle}>{preTitle}</p>}
            { title && <div className={titleClasses}>{titleIcon}{t(title)}</div> }
            { message && <div className={messageClasses} dangerouslySetInnerHTML={{ __html: t(message) }}/> }
            { !isEmpty(dynamicMessage) && <div className={classes.message}>{t(dynamicMessage.text, dynamicMessage.params)}</div>}
            { children }
            {footerActions && <div className={classes.actionFooter}>
                <div className={classes.leftSide}>
                    {footerLeftSideComponent}
                </div>
                <div className={classes.rightSide}>
                    { !!additionalButton?.showButton && <Button
                        testId={additionalButton?.testId}
                        className={additionalButtonClassNames}
                        onClick={additionalButton.onAdditionalButtonClick}
                        color={additionalButton?.color || 'secondary'}
                        disabled={additionalButton?.isDisabled
                            || isLoading(LOADING_AREAS.confirmationModalAdditionalButton) || additionalButton?.isAdditionalProcess}
                        spin={additionalButton?.isLoading
                            || isLoading(LOADING_AREAS.confirmationModalAdditionalButton) || additionalButton?.isAdditionalProcess}
                    >
                        {additionalButton?.label}
                    </Button> }
                    { rejectButton && <Button
                        testId={'cancel-button'}
                        className={`${classes.actionPanelButton} ${buttonClassName || ''}`}
                        onClick={shouldWaitUntilRejectActionCompleted ? rejectActionAsync : onRejectButtonClick}
                        color='secondary'
                        disabled={rejectButtonDisabled || isLoading(LOADING_AREAS.confirmationModalRejectButton) || isRejectInProcess}
                        spin={rejectButtonLoading || isLoading(LOADING_AREAS.confirmationModalRejectButton) || isRejectInProcess}
                    >
                        {t(rejectButtonLabel)}
                    </Button> }
                    { confirmButton && <Button
                        testId={'update-search-button'}
                        className={`${classes.actionPanelButton} ${buttonClassName || ''}`}
                        color='primary'
                        classes={classes.actionButton}
                        onClick={shouldWaitUntilActionCompleted ? confirmActionAsync : onConfirmButtonClick}
                        disabled={confirmButtonDisabled || isLoading(LOADING_AREAS.confirmationModalConfirmButton) || isConfirmInProcess}
                        spin={confirmButtonLoading || isLoading(LOADING_AREAS.confirmationModalConfirmButton) || isConfirmInProcess}
                    >
                        {t(confirmButtonLabel)}
                    </Button> }
                </div>
            </div>}
        </div>
    );
};

ConfirmationModal.propTypes = {
    className: PropTypes.string,
    messageClassName: PropTypes.string,
    titleClassName: PropTypes.string,
    title: PropTypes.string,
    message: PropTypes.string,
    confirmButton: PropTypes.bool,
    rejectButton: PropTypes.bool,
    autoFocus: PropTypes.bool,
    rejectButtonLabel: PropTypes.string,
    confirmButtonLabel: PropTypes.string,
    onRejectButtonClick: PropTypes.func,
    onConfirmButtonClick: PropTypes.func,
    rejectButtonLoading: PropTypes.bool,
    confirmButtonLoading: PropTypes.bool,
    rejectButtonDisabled: PropTypes.bool,
    confirmButtonDisabled: PropTypes.bool,
    footerLeftSideComponent: PropTypes.node,
    children: PropTypes.node,
    preTitle: PropTypes.string,
    titleIcon: PropTypes.node,
    footerActions: PropTypes.bool,
    smallPadding: PropTypes.bool,
    buttonClassName: PropTypes.string,
    additionalButton: PropTypes.shape({
        showButton: PropTypes.bool,
        testId: PropTypes.string,
        classNames: PropTypes.string,
        onAdditionalButtonClick: PropTypes.func,
        color: PropTypes.string,
        isDisabled: PropTypes.bool,
        isAdditionalProcess: PropTypes.bool,
        isLoading: PropTypes.bool,
        label: PropTypes.string,
    }),
};

export default ConfirmationModal;
